import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAdminTickets,
  getTicketStats,
  getTicketDetails,
  addTicketMessage,
  updateTicket,
  getTicketAttachment,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContainer';
import '../styles/admin-ui.css';
import './AdminTickets.css';

const STATUS_META = {
  open: { label: 'Open', chip: 'signal' },
  in_process: { label: 'In process', chip: 'warn' },
  solved: { label: 'Solved', chip: 'ok' },
  declined: { label: 'Declined', chip: 'danger' },
};

const PRIORITY_META = {
  low: { label: 'Low', chip: '' },
  normal: { label: 'Normal', chip: 'signal' },
  high: { label: 'High', chip: 'warn' },
  urgent: { label: 'Urgent', chip: 'danger' },
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return (
    date.toLocaleDateString() +
    ' ' +
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );
};

const AdminTickets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const messagesEndRef = useRef(null);

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    assigned: 'all',
  });
  const [query, setQuery] = useState('');

  const [activeTicket, setActiveTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [messageAttachment, setMessageAttachment] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    if (activeTicket?.messages?.length) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTicket?.messages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiFilters = {};
      if (filters.status !== 'all') apiFilters.status = filters.status;
      if (filters.priority !== 'all') apiFilters.priority = filters.priority;
      if (filters.assigned !== 'all') apiFilters.assigned = filters.assigned;

      const [ticketsData, statsData] = await Promise.all([
        getAdminTickets(apiFilters),
        getTicketStats(),
      ]);
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      setStats(statsData || null);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      showError(error.message || t('adminTickets.loadError', 'Failed to load tickets'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((ticket) => {
      return (
        ticket.ticket_number?.toLowerCase().includes(q) ||
        ticket.subject?.toLowerCase().includes(q) ||
        ticket.user_email?.toLowerCase().includes(q) ||
        ticket.username?.toLowerCase().includes(q) ||
        ticket.category_name?.toLowerCase().includes(q)
      );
    });
  }, [tickets, query]);

  const openTicketDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const detail = await getTicketDetails(ticketId);
      setActiveTicket(detail);
    } catch (error) {
      showError(error.message || t('adminTickets.detailError', 'Failed to load ticket'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTicketDetail = () => {
    setActiveTicket(null);
    setNewMessage('');
    setMessageAttachment(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showError(t('tickets.fileTooLarge', 'File too large. Maximum size is 2MB'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = String(event.target.result).split(',')[1];
      setMessageAttachment({
        name: file.name,
        data: base64,
        type: file.type,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!activeTicket || !newMessage.trim()) return;

    setSendingMessage(true);
    try {
      await addTicketMessage(activeTicket.id, {
        message: newMessage.trim(),
        attachment_name: messageAttachment?.name || '',
        attachment_data: messageAttachment?.data || '',
        attachment_type: messageAttachment?.type || '',
      });

      setNewMessage('');
      setMessageAttachment(null);

      const detail = await getTicketDetails(activeTicket.id);
      setActiveTicket(detail);
      loadData();
      showSuccess(t('tickets.messageSent', 'Message sent'));
    } catch (error) {
      showError(error.message || t('adminTickets.replyError', 'Failed to send reply'));
    } finally {
      setSendingMessage(false);
    }
  };

  const persistTicketUpdate = async (payload, successMessage) => {
    if (!activeTicket) return;
    setUpdating(true);
    try {
      await updateTicket(activeTicket.id, payload);
      const detail = await getTicketDetails(activeTicket.id);
      setActiveTicket(detail);
      loadData();
      if (successMessage) showSuccess(successMessage);
    } catch (error) {
      showError(error.message || t('adminTickets.updateError', 'Failed to update ticket'));
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = (status) => {
    persistTicketUpdate({ status }, t('tickets.statusUpdated', 'Status updated'));
  };

  const handlePriorityChange = (priority) => {
    persistTicketUpdate({ priority }, t('tickets.priorityUpdated', 'Priority updated'));
  };

  const handleAssignToMe = () => {
    if (!user?.id) {
      showError(t('adminTickets.noUser', 'Current admin user not available'));
      return;
    }
    persistTicketUpdate(
      { assigned_admin_id: user.id },
      t('adminTickets.assigned', 'Ticket assigned to you')
    );
  };

  const handleDownloadAttachment = async (message) => {
    if (!message?.id) return;
    setDownloadingAttachment(message.id);
    try {
      const attachment = await getTicketAttachment(message.id);
      if (!attachment?.data) {
        throw new Error(t('adminTickets.attachmentMissing', 'Attachment data not available'));
      }

      const mime = attachment.type || message.attachment_type || 'application/octet-stream';
      const binary = atob(attachment.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name || message.attachment_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      showError(error.message || t('adminTickets.attachmentError', 'Failed to download attachment'));
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const statusFilters = [
    { key: 'all', label: t('adminTickets.allStatus', 'All') },
    { key: 'open', label: t('adminTickets.open', 'Open') },
    { key: 'in_process', label: t('adminTickets.inProcess', 'In process') },
    { key: 'solved', label: t('adminTickets.solved', 'Solved') },
    { key: 'declined', label: t('adminTickets.declined', 'Declined') },
  ];

  return (
    <div className="aui-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← {t('common.back', 'Back to Admin')}
      </button>

      <div className="aui-hero">
        <div>
          <p className="aui-kicker">{t('adminTickets.kicker', 'Support console')}</p>
          <h1>{t('adminTickets.title', 'Support tickets')}</h1>
          <p className="aui-lead">
            {t(
              'adminTickets.subtitle',
              'Triage requests, assign ownership, and reply with attachments.'
            )}
          </p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={loadData}>
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {stats && (
        <div className="aui-stats">
          <article className="aui-stat">
            <span>{t('adminTickets.open', 'Open')}</span>
            <strong>{stats.open || 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('adminTickets.inProcess', 'In process')}</span>
            <strong>{stats.in_process || 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('adminTickets.solved', 'Solved')}</span>
            <strong>{stats.solved || 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('adminTickets.declined', 'Declined')}</span>
            <strong>{stats.declined || 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('adminTickets.unassigned', 'Unassigned')}</span>
            <strong>{stats.unassigned_count || 0}</strong>
          </article>
          <article className="aui-stat">
            <span>{t('adminTickets.today', 'Today')}</span>
            <strong>{stats.today_count || 0}</strong>
          </article>
        </div>
      )}

      <div className={`aui-tickets-workspace ${activeTicket ? 'has-detail' : ''}`}>
        <aside className={`aui-tickets-list ${activeTicket ? 'is-hidden-mobile' : ''}`}>
          <div className="aui-toolbar">
            <div className="aui-filters" role="tablist">
              {statusFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  role="tab"
                  aria-selected={filters.status === filter.key}
                  className={`aui-filter ${filters.status === filter.key ? 'is-active' : ''}`}
                  onClick={() => setFilters((prev) => ({ ...prev, status: filter.key }))}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className="aui-tickets-controls">
            <select
              className="aui-search"
              value={filters.priority}
              onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
            >
              <option value="all">{t('adminTickets.allPriority', 'All priority')}</option>
              <option value="low">{t('adminTickets.low', 'Low')}</option>
              <option value="normal">{t('adminTickets.normal', 'Normal')}</option>
              <option value="high">{t('adminTickets.high', 'High')}</option>
              <option value="urgent">{t('adminTickets.urgent', 'Urgent')}</option>
            </select>
            <select
              className="aui-search"
              value={filters.assigned}
              onChange={(e) => setFilters((prev) => ({ ...prev, assigned: e.target.value }))}
            >
              <option value="all">{t('adminTickets.allAssigned', 'All assignees')}</option>
              <option value="me">{t('adminTickets.assignedToMe', 'Assigned to me')}</option>
              <option value="unassigned">{t('adminTickets.unassigned', 'Unassigned')}</option>
            </select>
            <input
              className="aui-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('adminTickets.search', 'Search tickets…')}
            />
          </div>

          {loading ? (
            <div className="aui-empty">
              <div className="aui-spinner" />
              <p>{t('common.loading', 'Loading…')}</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="aui-empty">
              <h3>{t('adminTickets.noTickets', 'No tickets found')}</h3>
              <p>{t('adminTickets.noTicketsDesc', 'Adjust filters or wait for new support requests.')}</p>
            </div>
          ) : (
            <ul className="aui-ticket-list">
              {filteredTickets.map((ticket) => {
                const status = STATUS_META[ticket.status] || STATUS_META.open;
                const priority = PRIORITY_META[ticket.priority] || PRIORITY_META.normal;
                return (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      className={`aui-ticket-item ${activeTicket?.id === ticket.id ? 'is-active' : ''}`}
                      onClick={() => openTicketDetail(ticket.id)}
                    >
                      <div className="aui-ticket-top">
                        <span className="aui-ticket-id">#{ticket.ticket_number}</span>
                        <span className={`aui-chip ${status.chip}`}>{status.label}</span>
                      </div>
                      <strong>{ticket.subject}</strong>
                      <div className="aui-ticket-meta">
                        <span>{ticket.user_email || ticket.username || '—'}</span>
                        {ticket.category_name && <span>{ticket.category_name}</span>}
                        <span className={`aui-chip ${priority.chip}`}>{priority.label}</span>
                        <span>
                          {ticket.message_count || 0} {t('tickets.messages', 'messages')}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <div className={`aui-tickets-detail ${activeTicket ? 'is-open' : ''}`}>
          {!activeTicket && !detailLoading ? (
            <div className="aui-empty">
              <h3>{t('adminTickets.selectTicket', 'Select a ticket')}</h3>
              <p>
                {t(
                  'adminTickets.selectTicketDesc',
                  'Choose a conversation from the list to review messages and reply.'
                )}
              </p>
            </div>
          ) : detailLoading && !activeTicket ? (
            <div className="aui-empty">
              <div className="aui-spinner" />
            </div>
          ) : (
            <>
              <header className="aui-ticket-header">
                <button type="button" className="aui-back mobile-only" onClick={closeTicketDetail}>
                  ← {t('common.back', 'Back')}
                </button>
                <div className="aui-ticket-header-main">
                  <div className="aui-ticket-idrow">
                    <span>#{activeTicket.ticket_number}</span>
                    <span className={`aui-chip ${(STATUS_META[activeTicket.status] || {}).chip || ''}`}>
                      {(STATUS_META[activeTicket.status] || {}).label || activeTicket.status}
                    </span>
                    <span className={`aui-chip ${(PRIORITY_META[activeTicket.priority] || {}).chip || ''}`}>
                      {(PRIORITY_META[activeTicket.priority] || {}).label || activeTicket.priority}
                    </span>
                  </div>
                  <h2>{activeTicket.subject}</h2>
                  <p>
                    {activeTicket.user_email || activeTicket.username}
                    {activeTicket.category_name ? ` · ${activeTicket.category_name}` : ''}
                    {activeTicket.assigned_admin
                      ? ` · ${t('adminTickets.assignee', 'Assigned')}: ${activeTicket.assigned_admin}`
                      : ` · ${t('adminTickets.unassigned', 'Unassigned')}`}
                  </p>
                </div>
                <button type="button" className="aui-btn ghost" onClick={closeTicketDetail}>
                  {t('common.close', 'Close')}
                </button>
              </header>

              <div className="aui-ticket-controls">
                <label>
                  {t('adminTickets.status', 'Status')}
                  <select
                    value={activeTicket.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updating}
                  >
                    <option value="open">Open</option>
                    <option value="in_process">In process</option>
                    <option value="solved">Solved</option>
                    <option value="declined">Declined</option>
                  </select>
                </label>
                <label>
                  {t('adminTickets.priority', 'Priority')}
                  <select
                    value={activeTicket.priority}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                    disabled={updating}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </label>
                <div className="aui-ticket-assign">
                  <span>
                    {activeTicket.assigned_admin
                      ? `${t('adminTickets.assignee', 'Assigned')}: ${activeTicket.assigned_admin}`
                      : t('adminTickets.unassigned', 'Unassigned')}
                  </span>
                  <button
                    type="button"
                    className="aui-btn ghost"
                    onClick={handleAssignToMe}
                    disabled={
                      updating ||
                      !user?.id ||
                      Number(activeTicket.assigned_admin_id) === Number(user.id)
                    }
                  >
                    {Number(activeTicket.assigned_admin_id) === Number(user?.id)
                      ? t('adminTickets.assignedToYou', 'Assigned to you')
                      : t('adminTickets.assignToMe', 'Assign to me')}
                  </button>
                </div>
              </div>

              <div className="aui-ticket-messages">
                {activeTicket.messages?.map((msg) => (
                  <article
                    key={msg.id}
                    className={`aui-ticket-message ${msg.is_admin_reply ? 'is-admin' : 'is-user'}`}
                  >
                    <div className="aui-ticket-message-meta">
                      <strong>
                        {msg.is_admin_reply
                          ? msg.username || t('tickets.supportTeam', 'Support')
                          : msg.username || activeTicket.user_email}
                      </strong>
                      {msg.is_admin_reply && (
                        <span className="aui-chip signal">{t('adminTickets.support', 'Support')}</span>
                      )}
                      <time>{formatDate(msg.created_at)}</time>
                    </div>
                    <div className="aui-ticket-message-body">{msg.message}</div>
                    {msg.attachment_name && (
                      <button
                        type="button"
                        className="aui-ticket-attachment"
                        onClick={() => handleDownloadAttachment(msg)}
                        disabled={downloadingAttachment === msg.id}
                      >
                        {downloadingAttachment === msg.id
                          ? t('common.downloading', 'Downloading…')
                          : msg.attachment_name}
                      </button>
                    )}
                  </article>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <form className="aui-ticket-reply" onSubmit={handleSendMessage}>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('adminTickets.replyPlaceholder', 'Type your response…')}
                  rows={3}
                />
                <div className="aui-ticket-reply-bar">
                  <label className="aui-btn ghost aui-attach-btn">
                    {t('tickets.attach', 'Attach')}
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      hidden
                    />
                  </label>
                  {messageAttachment && (
                    <div className="aui-file-pill">
                      <span>{messageAttachment.name}</span>
                      <button type="button" onClick={() => setMessageAttachment(null)}>
                        ×
                      </button>
                    </div>
                  )}
                  <button
                    type="submit"
                    className="aui-btn primary"
                    disabled={sendingMessage || !newMessage.trim()}
                  >
                    {sendingMessage
                      ? t('common.sending', 'Sending…')
                      : t('tickets.send', 'Send')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTickets;
